import type { TableName } from "../../../../database-strings";
import { isSample } from "../../../../database-strings";
import type {
    RowIndex,
    UserSpecificRow} from "../../../../Database";
import {
    postAddRowToTableAction,
    postSetColumnsInRowAction,
    postDeleteRowAction
} from "../../../../Database";
import { logError } from "../../../../debug-print";
import type { DocumentData } from "../../../../Database/core";
import type { PrimitiveValue } from "../../../computation-types";
import type { DebounceKey, DebounceContextFreeArguments } from ".";
import { DurableStorageControllerBase } from ".";
import type { BaseRowIndex } from "../../../../firebase-function-types";

export class DummyDurableStorageController extends DurableStorageControllerBase {
    public async addRowToTable(
        tableName: TableName,
        _rowIndex: RowIndex | undefined,
        values: Record<string, PrimitiveValue>
    ): Promise<boolean> {
        const database = this._accessors.database();
        const appID = this._accessors.appID();
        const appFacilities = this._accessors.appFacilities();
        const fields = this.convertColumnValuesToFields(tableName, values);

        await database.enterNetworkConnectionLock();

        try {
            const authUserID = await appFacilities.getAuthUserID();
            await postAddRowToTableAction(database, appID, authUserID, this.deviceID, tableName, fields);
            return true;
        } catch (e: unknown) {
            logError("Posting add row to table failed", e);
            // We don't report write errors in sample apps
            return !isSample(appID);
        } finally {
            database.exitNetworkConnectionLock();
        }
    }

    private processSetColumnsInRowDebounce = async (key: DebounceKey, mapValue: DebounceContextFreeArguments) => {
        const { appID, appUserID, tableName, rowIndex } = key;
        const { values, fromBuilder, fromDataEditor } = mapValue;
        const database = this._accessors.database();
        const fields = this.convertColumnValuesToFields(tableName, values);
        try {
            const authUserID = await this._accessors.appFacilities().getAuthUserID();
            return await postSetColumnsInRowAction(
                database,
                appID,
                authUserID,
                appUserID,
                this.deviceID,
                tableName,
                rowIndex,
                fields,
                fromBuilder,
                fromDataEditor
            );
        } finally {
            database.exitNetworkConnectionLock();
        }
    };

    public handleUserAgnosticDocumentUpdate(
        _tableName: TableName,
        _rowIndex: BaseRowIndex,
        _rowVersion: number,
        doc: DocumentData
    ): DocumentData {
        return doc;
    }

    public handleUserSpecificDocumentUpdate(
        _tableName: TableName,
        _rowIndex: BaseRowIndex,
        _rowVersion: number | undefined,
        doc: DocumentData
    ): DocumentData {
        return doc;
    }

    public handleDocumentDeletion(_tableName: TableName, _rowIndex: RowIndex, _version: number): void {
        // This method intentionally left blank
    }

    public handleUserSpecificDataDeletion(_tableName: TableName, _rowIndex: RowIndex): DocumentData | undefined {
        // meh
        return undefined;
    }

    public resetUserSpecificData(): void {
        // This method intentionally left blank
    }

    public pendingUserSpecificUpdates(_tableName: TableName, _userAgnosticRow: DocumentData): UserSpecificRow[] {
        return [];
    }

    public async setColumnsInRow(
        tableName: TableName,
        rowIndex: RowIndex,
        values: Record<string, PrimitiveValue>,
        withDebounce: boolean,
        fromDataEditor: boolean
    ): Promise<void> {
        const database = this._accessors.database();
        const appID = this._accessors.appID();
        const appUserID = this._accessors.appUserID();
        const appFacilities = this._accessors.appFacilities();

        await database.enterNetworkConnectionLock();
        try {
            const authUserID = await appFacilities.getAuthUserID();

            if (withDebounce) {
                await this.debounceOperation({
                    appID,
                    appUserID,
                    tableName,
                    rowIndex,
                    values,
                    fromBuilder: this.isBuilder,
                    fromDataEditor,
                    executionTarget: this.processSetColumnsInRowDebounce,
                    onEntryCreation: () => database.enterNetworkConnectionLock(),
                    debounceTimeout: 3000,
                    jobID: undefined,
                });
            } else {
                const fields = this.convertColumnValuesToFields(tableName, values);
                await postSetColumnsInRowAction(
                    database,
                    appID,
                    authUserID,
                    appUserID,
                    this.deviceID,
                    tableName,
                    rowIndex,
                    fields,
                    this.isBuilder,
                    fromDataEditor
                );
            }
        } finally {
            // Note that the debounced setColumnsInRow also takes a network connection lock
            // the first time a debounce entry is added. This is a fully re-entrant
            // operation, so we can always release the lock we took here.
            database.exitNetworkConnectionLock();
        }
    }

    public async deleteRowAtIndex(tableName: TableName, rowIndex: RowIndex, fromDataEditor: boolean): Promise<void> {
        const database = this._accessors.database();
        const appFacilities = this._accessors.appFacilities();
        const appID = this._accessors.appID();
        const appUserID = this._accessors.appUserID();

        await database.enterNetworkConnectionLock();
        try {
            const authUserID = await appFacilities.getAuthUserID();

            await postDeleteRowAction(
                database,
                appID,
                authUserID,
                appUserID,
                this.deviceID,
                tableName,
                rowIndex,
                this.isBuilder,
                fromDataEditor
            );
        } finally {
            database.exitNetworkConnectionLock();
        }
    }

    public retire(): void {
        return;
    }
}
